import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { AlertTriangle, Lightbulb, Info } from "lucide-react";

const Chemistry = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Water Chemistry 101" 
        description="Understanding what each chemical reading means and why it matters"
      >
        <section className="space-y-8">
          {/* Quick Reference */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Ideal Ranges</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-muted">
                    <th className="border p-3 text-left font-semibold">Parameter</th>
                    <th className="border p-3 text-left font-semibold">Ideal Range</th>
                    <th className="border p-3 text-left font-semibold">Why It Matters</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr>
                    <td className="border p-3 font-medium text-foreground">Free Chlorine</td>
                    <td className="border p-3">2–4 ppm</td>
                    <td className="border p-3">Kills bacteria, prevents algae</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border p-3 font-medium text-foreground">pH</td>
                    <td className="border p-3">7.2–7.6</td>
                    <td className="border p-3">Chlorine effectiveness + comfort</td>
                  </tr>
                  <tr>
                    <td className="border p-3 font-medium text-foreground">Total Alkalinity</td>
                    <td className="border p-3">80–120 ppm</td>
                    <td className="border p-3">Stabilizes pH, prevents bounce</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border p-3 font-medium text-foreground">Calcium Hardness</td>
                    <td className="border p-3">200–400 ppm</td>
                    <td className="border p-3">Protects plaster, prevents scale</td>
                  </tr>
                  <tr>
                    <td className="border p-3 font-medium text-foreground">Cyanuric Acid (CYA)</td>
                    <td className="border p-3">30–50 ppm</td>
                    <td className="border p-3">UV protection for chlorine</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border p-3 font-medium text-foreground">Salt (if applicable)</td>
                    <td className="border p-3">3000–3500 ppm</td>
                    <td className="border p-3">Salt cell chlorine production</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* pH Deep Dive */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">pH: The Foundation</h2>
            <p className="text-muted-foreground mb-4">
              pH measures how acidic or basic the water is. It's the most frequently adjusted parameter 
              because it directly affects everything else.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                <h4 className="font-semibold text-destructive mb-2">Low pH (&lt;7.2)</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Corrosive to equipment and plaster</li>
                  <li>• Eye and skin irritation</li>
                  <li>• Chlorine works faster but dissipates quickly</li>
                </ul>
                <p className="text-sm mt-2"><strong>Fix:</strong> Add soda ash or sodium carbonate</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h4 className="font-semibold text-amber-600 mb-2">High pH (&gt;7.6)</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Chlorine becomes less effective</li>
                  <li>• Scale formation on surfaces</li>
                  <li>• Cloudy water</li>
                </ul>
                <p className="text-sm mt-2"><strong>Fix:</strong> Add muriatic acid or dry acid</p>
              </div>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> At pH 7.2, about 65% of your chlorine is active. 
                  At pH 8.0, only about 20% is active. That's why pH control is critical — you could have plenty of chlorine 
                  that isn't working.
                </p>
              </div>
            </div>
          </div>

          {/* Alkalinity */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Total Alkalinity: The Buffer</h2>
            <p className="text-muted-foreground mb-4">
              Alkalinity is pH's bodyguard. It prevents rapid pH swings by buffering the water against changes.
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• <strong className="text-foreground">Low alkalinity</strong> = pH bounces wildly, difficult to stabilize</li>
              <li>• <strong className="text-foreground">High alkalinity</strong> = pH resists change, tends to drift high</li>
              <li>• <strong className="text-foreground">Fix low:</strong> Add sodium bicarbonate (baking soda)</li>
              <li>• <strong className="text-foreground">Fix high:</strong> Add muriatic acid (lowers both TA and pH)</li>
            </ul>
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-accent-foreground mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Adjustment Order:</strong> Always adjust alkalinity first, then pH. 
                  The alkalinity level affects how pH behaves.
                </p>
              </div>
            </div>
          </div>

          {/* Calcium Hardness */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Calcium Hardness</h2>
            <p className="text-muted-foreground mb-4">
              Water naturally wants to hold a certain amount of dissolved calcium. If it doesn't have enough, 
              it will take it from plaster, grout, and equipment. If it has too much, it will deposit it as scale.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Low Calcium</h4>
                <p className="text-sm text-muted-foreground">
                  Aggressive water that etches plaster and corrodes metals. Add calcium chloride.
                </p>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">High Calcium</h4>
                <p className="text-sm text-muted-foreground">
                  Scale deposits on tiles, inside pipes, and on salt cells. Partial drain and refill may be needed.
                </p>
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">San Diego Note:</strong> Our tap water is naturally hard (high calcium). 
                  Monitor calcium levels closely — they tend to creep up over time, especially with evaporation.
                </p>
              </div>
            </div>
          </div>

          {/* CYA */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Cyanuric Acid (CYA / Stabilizer)</h2>
            <p className="text-muted-foreground mb-4">
              CYA acts as sunscreen for chlorine. Without it, UV rays destroy chlorine within hours. 
              But too much CYA traps chlorine, making it ineffective.
            </p>
            <div className="bg-card border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-3">CYA Guidelines</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• <strong className="text-foreground">0-30 ppm:</strong> Chlorine burns off too fast, add stabilizer</li>
                <li>• <strong className="text-foreground">30-50 ppm:</strong> Ideal range for most pools</li>
                <li>• <strong className="text-foreground">50-80 ppm:</strong> Acceptable, but watch chlorine effectiveness</li>
                <li>• <strong className="text-foreground">80+ ppm:</strong> "Chlorine lock" — chlorine present but ineffective. Partial drain required.</li>
              </ul>
            </div>
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Warning:</strong> CYA accumulates over time (stabilized chlorine tablets add it). 
                  There's no chemical to remove it — only dilution through draining works.
                </p>
              </div>
            </div>
          </div>

          {/* Free vs Combined Chlorine */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Free vs Combined Chlorine</h2>
            <p className="text-muted-foreground mb-4">
              Not all chlorine in the pool is working. Understanding the difference is key to proper treatment.
            </p>
            <div className="bg-card border rounded-lg p-4">
              <ul className="space-y-3 text-muted-foreground">
                <li>
                  <strong className="text-foreground">Free Chlorine (FC):</strong> Active chlorine available to kill contaminants. 
                  This is what you want at 2-4 ppm.
                </li>
                <li>
                  <strong className="text-foreground">Combined Chlorine (CC):</strong> Chlorine that's already reacted with contaminants 
                  (chloramines). This causes the "chlorine smell" and eye irritation.
                </li>
                <li>
                  <strong className="text-foreground">Total Chlorine:</strong> FC + CC. If total is higher than free, 
                  you have combined chlorine that needs to be eliminated.
                </li>
              </ul>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> If a pool "smells like chlorine," it usually means 
                  there's too much combined chlorine, not too much free chlorine. The fix is shocking the pool.
                </p>
              </div>
            </div>
          </div>

          {/* Testing Workflow */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Testing Workflow</h2>
            <div className="bg-card border rounded-lg p-6">
              <ol className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <span>Collect sample from elbow-depth, away from return jets</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <span>Test free chlorine and pH first — these are adjusted most often</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <span>Test alkalinity weekly or when pH is unstable</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <span>Test CYA monthly (it changes slowly)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">5</span>
                  <span>Log all readings in the App before adding chemicals</span>
                </li>
              </ol>
            </div>
          </div>

          {/* Common Mistakes */}
          <div className="bg-destructive/10 border border-destructive rounded-lg p-6">
            <h3 className="text-lg font-semibold text-destructive mb-3">Common Mistakes</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Adding chemicals without testing first</li>
              <li>• Adjusting pH before alkalinity</li>
              <li>• Adding acid and chlorine at the same time (wait 4+ hours)</li>
              <li>• Ignoring rising CYA levels until it's too high</li>
              <li>• Testing in the wrong location (near returns or skimmers)</li>
            </ul>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Chemistry;